import React, { useState, useEffect } from 'react';
import { Award, Star, Flame, BookOpen, Users, Zap, Target, Gift } from 'lucide-react';

export default function AchievementDisplay({ achievements = { earned: [], available: [] }, isLoading = false }) {

  const getIconComponent = (iconType) => {
    const iconMap = {
      book: BookOpen,
      flame: Flame,
      star: Star,
      zap: Zap,
      users: Users,
      target: Target,
      gift: Gift
    };
    const IconComponent = iconMap[iconType] || Award;
    return <IconComponent className="w-4 h-4" />;
  };

  const getRarityColors = (rarity) => {
    switch (rarity) {
      case 'common':
        return {
          bg: 'bg-stone-100',
          border: 'border-stone-300',
          text: 'text-stone-600',
          icon: 'text-stone-500'
        };
      case 'uncommon':
        return {
          bg: 'bg-emerald-100',
          border: 'border-emerald-300',
          text: 'text-emerald-700',
          icon: 'text-emerald-600'
        };
      case 'rare':
        return {
          bg: 'bg-azure-100',
          border: 'border-azure-300',
          text: 'text-azure-700',
          icon: 'text-azure-600'
        };
      case 'legendary':
        return {
          bg: 'bg-violet-100',
          border: 'border-violet-300',
          text: 'text-violet-700',
          icon: 'text-violet-600'
        };
      default:
        return {
          bg: 'bg-stone-100',
          border: 'border-stone-300',
          text: 'text-stone-600',
          icon: 'text-stone-500'
        };
    }
  };

  const earnedAchievements = achievements.earned || [];
  const availableAchievements = achievements.available || [];

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-indigo-200 shadow-sm border-l-4 border-l-indigo-500">
        <div className="flex items-center gap-2 mb-4">
          <Award className="w-5 h-5 text-indigo-600" />
          <h2 className="text-lg font-semibold text-slate-900">Achievements</h2>
        </div>
        
        <div className="text-center py-8 text-stone-500">
          <Award className="w-12 h-12 mx-auto mb-4 text-stone-300 animate-pulse" />
          <p>Loading your achievements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-5 border border-indigo-200 shadow-sm border-l-4 border-l-indigo-500">
      <div className="flex items-center gap-2 mb-4">
        <Award className="w-5 h-5 text-indigo-600" />
        <h2 className="text-lg font-semibold text-slate-900">Achievements</h2>
        <div className="ml-auto bg-indigo-100 text-indigo-700 text-xs font-semibold px-2 py-1 rounded-full">
          {earnedAchievements.length} earned
        </div>
      </div>

      {/* Recently Earned Achievements */}
      {earnedAchievements.length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" />
            Recently Earned
          </h3>
          
          <div className="grid grid-cols-1 gap-3">
            {earnedAchievements.slice(0, 2).map((achievement) => {
              const colors = getRarityColors(achievement.rarity);
              return (
                <div 
                  key={achievement.id}
                  className={`p-3 rounded-lg border ${colors.bg} ${colors.border} flex items-center gap-3`}
                >
                  <div className={`w-8 h-8 rounded-lg bg-white flex items-center justify-center ${colors.icon} border ${colors.border}`}>
                    {getIconComponent(achievement.icon)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-sm ${colors.text}`}>
                      {achievement.name}
                    </h4>
                    <p className="text-xs text-stone-600 mb-1">
                      {achievement.description}
                    </p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`font-medium ${colors.text}`}>
                        +{achievement.points} points
                      </span>
                      {achievement.earnedDate && (
                        <>
                          <span className="text-stone-400">•</span>
                          <span className="text-stone-500">
                            {new Date(achievement.earnedDate).toLocaleDateString()}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Achievements */}
      {availableAchievements.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-stone-500" />
            Available to Earn
          </h3>
          
          <div className="grid grid-cols-1 gap-3">
            {availableAchievements.slice(0, 3).map((achievement) => {
              const colors = getRarityColors(achievement.rarity);
              const hasProgress = achievement.progress !== undefined && achievement.target !== undefined;
              const progressPercentage = hasProgress ? (achievement.progress / achievement.target) * 100 : 0;
              
              return (
                <div 
                  key={achievement.id}
                  className="p-3 rounded-lg border border-stone-200 bg-stone-50 flex items-center gap-3 opacity-70 hover:opacity-100 transition-opacity"
                >
                  <div className="w-8 h-8 rounded-lg bg-stone-200 flex items-center justify-center text-stone-400 border border-stone-300">
                    {getIconComponent(achievement.icon)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-stone-700">
                      {achievement.name}
                    </h4>
                    <p className="text-xs text-stone-600 mb-1">
                      {achievement.description}
                    </p>
                    
                    {hasProgress ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-stone-200 rounded-full h-1.5 overflow-hidden">
                          <div 
                            className={`h-full ${colors.bg.replace('bg-', 'bg-').replace('-100', '-400')} transition-all duration-300`}
                            style={{ width: `${progressPercentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-stone-500 font-medium">
                          {achievement.progress}/{achievement.target}
                        </span>
                      </div>
                    ) : (
                      <div className="text-xs font-medium text-stone-500">
                        +{achievement.points} points
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* View All Button */}
      <div className="mt-4 text-center">
        <button className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1 mx-auto">
          <Award className="w-4 h-4" />
          View All Achievements
        </button>
      </div>
    </div>
  );
}